import { getInitData, getDevUserHeader } from "./telegram";
import * as demoStore from "./demo-store";
import { isDemoMode } from "./demo";

export type Lang = "en" | "ru";
export type Tier = "cheap" | "mid" | "premium" | "joker";

export interface IdeaDTO {
  id: number;
  model: string;
  model_label: string;
  tier: Tier;
  title: string;
  // Variant-C operator-angle fields (current schema).
  today_they: string;
  instead_they_could: string;
  who_pays_and_how_much: string;
  distribution: string;
  the_giant_in_the_room: string;
  // Legacy v2 fields, empty on new spins, populated only on saved
  // ideas from before the schema swap.
  subtitle: string;
  who_pays: string;
  what_to_build: string;
  how_to_get_customers: string;
  main_risk: string;
  language: Lang;
  created_at: number;
  saved: boolean;
  saved_at?: number;
}

export interface ProfileDTO {
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  language: Lang;
  about_text: string;
  total_spins: number;
  saved_count: number;
  // Balance snapshot from the worker. In demo mode `free_limit` is 0
  // (paid features hidden, there's no Telegram Stars outside the app).
  free_left: number;
  free_limit: number;
  credits: number;
}

export type PackId = "try" | "hot" | "founder";

export interface PackDTO {
  id: PackId;
  stars: number;
  credits: number;
  emoji: string;
  badge: "best" | "whale" | null;
}

export interface CreditsResponse {
  balance: {
    free_left: number;
    free_limit: number;
    credits: number;
    next_is_paid: boolean;
  };
  packs: PackDTO[];
}

export interface SpinResponse {
  idea: IdeaDTO;
  tier: Tier;
  model_label: string;
}

export type WildnessId = "safe" | "solid" | "bold" | "wild" | "unhinged";

/** Compact summary of an idea the user explicitly rejected. */
export interface RejectedSummary {
  title: string;
  subtitle?: string;
  reason?: string;
}

export interface SpinOptions {
  language?: Lang;
  model?: string;
  /** Reel 2, idea wildness. Worker translates to a system-prompt flavour. */
  wildness?: WildnessId;
  /** Reel 3, raw LLM temperature, clamped server-side to [0.3, 1.5]. */
  temperature?: number;
  /** Recent ideas the user thumbs-downed; the worker injects them as a
   *  "do NOT generate anything like these" block in the system prompt. */
  rejected?: RejectedSummary[];
}

export interface DiceFaceDisplay { id: string; label?: string }

export interface DiceRoll {
  experience:   DiceFaceDisplay;
  wedge:        DiceFaceDisplay;
  voice:        DiceFaceDisplay;
  horizon:      DiceFaceDisplay;
  competition:  DiceFaceDisplay;
  monetization: DiceFaceDisplay;
}

export interface Competitor {
  title: string;
  url: string;
  blurb?: string;
}

/** Events streamed from POST /api/spin/stream. */
export type SpinStreamEvent =
  | { type: "meta"; model: string; model_label: string; tier: Tier; language: Lang; dice: DiceRoll }
  | { type: "field"; key: keyof IdeaDTO; value: string | number }
  | { type: "idea"; idea: IdeaDTO }
  | { type: "competitors"; competitors: Competitor[] }
  | { type: "error"; code: string; status?: number; model?: string; model_label?: string; reason?: string; refunded?: boolean }
  | { type: "done" };

const BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

function headers(extra: HeadersInit = {}): HeadersInit {
  const initData = getInitData();
  return {
    "Content-Type": "application/json",
    ...(initData ? { Authorization: `tma ${initData}` } : {}),
    ...getDevUserHeader(),
    ...extra,
  };
}

async function jfetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    credentials: "include",
    headers: headers(init.headers),
  });
  if (!res.ok) {
    let body: any = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err: any = new Error(body?.error || `http_${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return (await res.json()) as T;
}

/**
 * Streaming spin. Yields events as soon as bytes arrive from the worker.
 * Caller is responsible for honouring AbortSignal and reacting to errors.
 */
export async function* spinStream(
  opts: SpinOptions & { signal?: AbortSignal } = {},
): AsyncGenerator<SpinStreamEvent> {
  const res = await fetch(BASE + "/api/spin/stream", {
    method: "POST",
    credentials: "include",
    headers: headers(),
    body: JSON.stringify({
      language: opts.language,
      model: opts.model,
      wildness: opts.wildness,
      temperature: opts.temperature,
      rejected: opts.rejected,
    }),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) {
    let body: any = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    yield { type: "error", code: body?.error || `http_${res.status}`, status: res.status };
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // SSE frames are delimited by a blank line.
    let i: number;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, i);
      buf = buf.slice(i + 2);
      let event = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (!data) continue;
      let parsed: any;
      try {
        parsed = JSON.parse(data);
      } catch {
        continue;
      }
      yield { type: event, ...parsed } as SpinStreamEvent;
      if (event === "done") return;
    }
  }
}

export const api = {
  me: async (): Promise<ProfileDTO> => {
    if (isDemoMode()) return demoStore.getProfile();
    return jfetch<ProfileDTO>("/api/me");
  },
  updateProfile: async (
    patch: Partial<Pick<ProfileDTO, "about_text" | "language">>,
  ): Promise<{ ok: boolean; about_text: string; language: Lang }> => {
    if (isDemoMode()) {
      const next = demoStore.updateProfile(patch);
      return { ok: true, about_text: next.about_text, language: next.language };
    }
    return jfetch<{ ok: boolean; about_text: string; language: Lang }>("/api/profile", {
      method: "POST",
      body: JSON.stringify(patch),
    });
  },
  spin: (opts: SpinOptions = {}) =>
    jfetch<SpinResponse>("/api/spin", {
      method: "POST",
      body: JSON.stringify(opts),
    }),
  // In demo we ignore the server-side search and just filter client-side
  // (caller already does that for prod responses anyway).
  saved: async (_q?: string): Promise<{ ideas: IdeaDTO[] }> => {
    if (isDemoMode()) return { ideas: demoStore.getSaved() };
    return jfetch<{ ideas: IdeaDTO[] }>(
      `/api/saved${_q ? `?q=${encodeURIComponent(_q)}` : ""}`,
    );
  },
  // Save always takes the full DTO. Demo needs it to rehydrate from
  // localStorage; prod only reads `idea.id`. Keeping a single signature
  // avoids the earlier `number | IdeaDTO` union that silently failed in
  // demo for the number branch.
  save: async (idea: IdeaDTO): Promise<{ ok: boolean }> => {
    if (isDemoMode()) {
      demoStore.addSaved(idea);
      return { ok: true };
    }
    return jfetch<{ ok: boolean }>(`/api/save/${idea.id}`, { method: "POST", body: "{}" });
  },
  unsave: async (spinId: number): Promise<{ ok: boolean }> => {
    if (isDemoMode()) {
      demoStore.removeSaved(spinId);
      return { ok: true };
    }
    return jfetch<{ ok: boolean }>(`/api/save/${spinId}`, { method: "DELETE" });
  },
  /**
   * Record one completed spin. In Telegram-mode it's a no-op (the worker
   * counts spins server-side via /api/spin/stream); in demo we bump the
   * local profile so the Profile tab's "X spins" counter reflects what
   * the user actually did.
   */
  recordSpin: async (): Promise<ProfileDTO | null> => {
    if (isDemoMode()) return demoStore.bumpSpinCounter();
    return null;
  },
  credits: async (): Promise<CreditsResponse> => {
    if (isDemoMode()) {
      const p = demoStore.getProfile();
      return {
        balance: {
          free_left: p.free_left,
          free_limit: p.free_limit,
          credits: p.credits,
          next_is_paid: p.free_left === 0 && p.credits > 0,
        },
        packs: [],
      };
    }
    return jfetch<CreditsResponse>("/api/credits");
  },
  buyInvoice: async (pack: PackId): Promise<{ invoice_link: string; pack: PackId }> =>
    jfetch<{ invoice_link: string; pack: PackId }>("/api/credits/invoice", {
      method: "POST",
      body: JSON.stringify({ pack }),
    }),
  /** Mint a session cookie from a Telegram Login Widget payload. */
  loginWithTelegram: (payload: Record<string, unknown>) =>
    jfetch<{ ok: true }>("/auth/telegram", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  /** Bot deep-link auth: kick off a one-shot token + return deep links. */
  botLoginStart: () =>
    jfetch<{ token: string; deep_link: string; web_link: string; ttl_seconds: number }>(
      "/auth/bot/start",
      { method: "POST", body: "{}" },
    ),
  /** Poll a pending bot-login token. Server sets the cookie on "ready". */
  botLoginCheck: (token: string) =>
    jfetch<{ status: "pending" | "ready" | "expired" }>(
      `/auth/bot/check?token=${encodeURIComponent(token)}`,
    ),
  logout: () => jfetch<{ ok: true }>("/auth/logout", { method: "POST", body: "{}" }),
  authConfig: () => jfetch<{ bot_username: string; web_origin: string }>("/auth/config"),
};

export function formatAgo(unix: number | undefined, lang: Lang): string {
  if (!unix) return "";
  const now = Date.now() / 1000;
  const diff = Math.max(0, now - unix);
  if (diff < 60) return lang === "ru" ? "только что" : "just now";
  if (diff < 3600) {
    const n = Math.floor(diff / 60);
    return lang === "ru" ? `${n} мин назад` : `${n}m ago`;
  }
  if (diff < 86400) {
    const n = Math.floor(diff / 3600);
    return lang === "ru" ? `${n} ч назад` : `${n}h ago`;
  }
  const n = Math.floor(diff / 86400);
  return lang === "ru" ? `${n} д назад` : `${n}d ago`;
}
